import i18n from "i18next";
import { useState } from "react";
import { useNavigate } from "react-router";
import { IconBell, IconCircleCheck, IconPencil, IconShare2, IconUserCheck, IconUserMinus } from "@tabler/icons-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/lilia-ui/popover";
import { testIds } from "@/testIds";
import { destination, mergeRows, rowTitle, whenShort, type NotificationRow } from "@/lib/notifications";
import { useNotifications } from "./useNotifications";

const T = testIds.notifications;

const ICON: Record<string, typeof IconBell> = {
  document_shared: IconShare2,
  invite_accepted: IconUserCheck,
  role_changed: IconPencil,
  access_removed: IconUserMinus,
};

/**
 * The bell (Olivia, 10 Oct): left of the avatar on desktop, in the Documents
 * bar on a phone. The unread count; a list with Mark all as read; rows of one
 * kind for one document within ten minutes merged. A click opens what the
 * notification is about (access removed opens nothing: the row says who).
 */
export function NotificationBell({ size = 30 }: { size?: number }) {
  const { items, unread, markRead, markAllRead, loading } = useNotifications();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const rows = mergeRows(items);

  const openRow = (r: NotificationRow) => {
    void markRead(r.items.filter((n) => !n.isRead).map((n) => n.id));
    const to = destination(r.head);
    if (to) {
      setOpen(false);
      navigate(to);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={T.bell}
          aria-label={unread ? i18n.t("shell:notifications.unreadLabel", { count: unread }) : i18n.t("shell:notifications.title")}
          title={i18n.t("shell:notifications.title")}
          className="relative flex items-center justify-center rounded-md text-[var(--lilia-text-secondary)] transition-colors hover:bg-[var(--lilia-hover)]"
          style={{ width: size, height: size }}
        >
          <IconBell size={16} />
          {unread > 0 && (
            <span
              data-testid={T.badge}
              className="absolute -right-0.5 -top-0.5 flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-[var(--lilia-danger,#c62828)] px-1 text-[10px] font-semibold leading-none text-white"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] max-w-[calc(100vw-24px)] p-0" data-testid={T.panel}>
        <div className="flex items-center justify-between border-b border-[var(--lilia-border)] px-3 py-2">
          <span className="text-[13px] font-semibold">{i18n.t("shell:notifications.title")}</span>
          <button
            type="button"
            data-testid={T.markAll}
            disabled={!unread}
            onClick={() => void markAllRead()}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[12px] text-[var(--lilia-primary)] hover:bg-[var(--lilia-hover)] disabled:text-[var(--lilia-text-muted)] disabled:hover:bg-transparent"
          >
            <IconCircleCheck size={13} /> {i18n.t("shell:notifications.markAll")}
          </button>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {!rows.length ? (
            <p data-testid={T.empty} className="px-3 py-6 text-center text-[12.5px] text-[var(--lilia-text-muted)]">
              {loading ? i18n.t("shell:notifications.loading") : i18n.t("shell:notifications.empty")}
            </p>
          ) : (
            <ul>
              {rows.map((r) => {
                const Icon = ICON[r.head.type] ?? IconBell;
                const clickable = !!destination(r.head);
                return (
                  <li key={r.head.id}>
                    <button
                      type="button"
                      data-testid={T.row}
                      data-type={r.head.type}
                      data-unread={r.unread ? "true" : "false"}
                      onClick={() => openRow(r)}
                      className={
                        "flex w-full items-start gap-2.5 px-3 py-2.5 text-left hover:bg-[var(--lilia-hover)] " +
                        (clickable ? "" : "cursor-default")
                      }
                    >
                      <Icon size={16} className="mt-0.5 shrink-0 text-[var(--lilia-text-secondary)]" />
                      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <span className={"text-[12.5px] leading-snug " + (r.unread ? "font-semibold text-[var(--lilia-text)]" : "text-[var(--lilia-text-secondary)]")}>
                          {rowTitle(r)}
                        </span>
                        {r.head.message && <span className="text-[12px] text-[var(--lilia-text-muted)]">{r.head.message}</span>}
                        <span className="text-[11px] text-[var(--lilia-text-muted)]">{whenShort(r.head.createdAt)}</span>
                      </span>
                      {r.unread && <span aria-label={i18n.t("shell:notifications.unread")} className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[var(--lilia-primary,#0a76a4)]" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
